import { html } from "htm/preact";
import { useEffect, useState } from "preact/hooks";
import { Icon } from "./Icon.js";
import { HolePlan } from "./HolePlan.js";
import { distancesToGreen } from "../../../shared/geo.js";
import { holePlan, isOnHole } from "../../../shared/holePlan.js";
import type { Fix } from "../gps.js";
import type { CoursePath, CourseShape, HoleGeo, Landmark } from "../../../shared/types.js";

const YARDS_PER_METRE = 1 / 0.9144;

/** Past this, the fix is loose enough that the yardage deserves to be read with
 * suspicion rather than trusted to the yard. */
const WEAK_FIX_YARDS = 15;

/**
 * How long "finding you" is allowed to stand before the screen gives up and says so.
 *
 * On our own clock rather than the browser's, because the browser cannot be relied on
 * to report the failure: with a permission prompt left unanswered, Chromium returns
 * neither a fix nor an error — not even after its own `timeout` elapses — and the watch
 * simply never calls back. Without this cap, that renders as "Finding you…" for the
 * rest of the round.
 *
 * Longer than the watch's own timeout, so a real error still gets to speak first.
 */
const GIVE_UP_MS = 22000;

/**
 * Live distance to the green, on the scorecard.
 *
 * Two rules run this component, and both are stricter than they look:
 *
 * 1. **Absent means gone.** No coordinates for this hole, no permission, or no fix yet
 *    and it renders `null` — not a placeholder, not a spinner, not an empty wrapper
 *    that keeps its padding. All spacing lives on the strip itself rather than as a gap
 *    on `.hole-card`, because a gap still applies around a zero-height child. With
 *    distances unavailable the scorecard is the scorecard, unchanged.
 * 2. **Scoring is never implicated.** The one message this shows says distances are off
 *    and scoring isn't.
 *
 * The reason this feature is back at all is that coverage is no longer someone else's
 * problem: a hole has coordinates because it was hand-traced in the course-map builder,
 * so "offered but empty" — the thing that sank the OpenStreetMap version in 9082149 —
 * cannot happen. The strip is only ever offered on a hole that already has a center.
 */
export function GreenDistance({
  geo,
  fix,
  enabled,
  unavailable,
  cardYards,
  teeName,
  onEnable,
  paths,
  landmarks,
  others,
  hazards,
  simulated,
  openByDefault,
}: {
  geo: HoleGeo | null;
  fix: Fix | null;
  enabled: boolean;
  unavailable: boolean;
  cardYards: number | null;
  teeName: string | null;
  onEnable: () => void;
  /** Course-level cart paths, passed straight through to the diagram. */
  paths?: CoursePath[];
  landmarks?: Landmark[];
  /** Course-level bunkers and water, likewise — the diagram decides which are live. */
  hazards?: CourseShape[];
  /** The course's other holes, likewise — the diagram's faint context layer. */
  others?: HoleGeo[];
  /**
   * Whether the fix was invented — by the builder's position simulator, testing this
   * screen off a sofa. Says so on the strip, because a number that looks real and isn't
   * is the one way this feature can actually mislead someone.
   *
   * A prop rather than a read of the `simulated` signal, because the builder's preview
   * passes a fix it holds itself and never writes to storage at all.
   */
  simulated?: boolean;
  /** Opens with the diagram already showing. For the builder preview, where the whole
   * reason the panel is on screen is to look at the drawing. */
  openByDefault?: boolean;
}) {
  // Whether the person asked for this *just now*, on this screen. It's what separates
  // feedback on a tap from an unexplained empty state: a cold fix is worth a word to
  // the player waiting for it, and worth nothing at all to a player who never asked.
  const [asked, setAsked] = useState(false);
  const [showPlan, setShowPlan] = useState(openByDefault === true);
  const [gaveUp, setGaveUp] = useState(false);

  const waiting = asked && !fix && !unavailable;
  useEffect(() => {
    if (!waiting) return undefined;
    const timer = setTimeout(() => setGaveUp(true), GIVE_UP_MS);
    return () => clearTimeout(timer);
  }, [waiting]);

  // No center, no distance worth showing. Front and back alone would be a yardage to
  // the edge of something whose middle we can't name.
  if (!geo || !geo.greenCenter) return null;

  const planAvailable = holePlan(geo) !== null;
  // A fix from somewhere that isn't this hole is not a yardage — see `isOnHole`. The
  // strip still renders, because the layout should not rearrange itself the moment you
  // walk onto the course; only the number is unknown.
  const live = enabled && fix && isOnHole(geo, fix.coords) ? fix : null;

  if (!enabled && !planAvailable) {
    return html`
      <button
        class="ghost gd-enable"
        onClick=${() => {
          setAsked(true);
          onEnable();
        }}
      >
        <${Icon} name="pin" size=${18} /> Show distance to the green
      </button>
    `;
  }

  const distances = live ? distancesToGreen(geo, live.coords) : null;
  const accuracy = live ? Math.round(live.accuracy * YARDS_PER_METRE) : null;
  const weak = accuracy !== null && accuracy > WEAK_FIX_YARDS;
  const hasDepth = distances?.front != null && distances?.back != null;

  /**
   * Why there is no number, in a few words, in the slot the accuracy chip normally
   * occupies. Ordered by what the player can do about it: permission first, then the
   * wait, then simply not being there yet.
   */
  const reason = !enabled
    ? "location off"
    : unavailable || gaveUp
      ? "no fix"
      : !fix
        ? asked
          ? "finding you…"
          : "location off"
        : "not at the hole";

  return html`
    <div
      class="green-distance"
      role="group"
      aria-label=${
        distances?.middle != null
          ? `${distances.middle} yards to the middle of the green, give or take ${accuracy}${
              simulated ? ", from a simulated position" : ""
            }`
          : `Distance to the green unavailable — ${reason}`
      }
    >
      <div class="gd-live">
        <span class=${`gd-yards ${distances?.middle == null ? "is-unknown" : ""}`}>
          ${distances?.middle ?? "—"}
        </span>
        <span class="gd-unit">yd<small>to green</small></span>
        ${accuracy !== null
          ? html`<span class=${`chip gd-acc ${weak ? "chip-warn" : ""}`}>
              ±${accuracy} yd${weak ? " · weak" : ""}
            </span>`
          : html`<span class="chip gd-acc gd-reason">${reason}</span>`}
        ${simulated && html`<span class="chip chip-warn gd-sim">simulated</span>`}
        ${planAvailable &&
        html`
          <button
            class=${`icon-btn gd-plan-toggle ${showPlan ? "is-open" : ""}`}
            aria-expanded=${showPlan}
            aria-label=${showPlan ? "Hide the hole" : "Show the hole"}
            onClick=${() => setShowPlan((v) => !v)}
          >
            <${Icon} name="down" size=${20} />
          </button>
        `}
      </div>

      ${/* Only ever shown alongside a live reading. The card's yardage moves down here,
           relabelled, so two different yardages are never presented alike on one card —
           but with no live number there is nothing to tell it apart from, the hole's own
           meta row above is already showing it, and repeating it is just noise. */
      live &&
      (hasDepth || cardYards) &&
      html`
        <div class="gd-meta">
          ${hasDepth &&
          html`<span class="chip">Front ${distances!.front} · Back ${distances!.back}</span>`}
          ${cardYards &&
          html`<span class="chip">Card ${cardYards} yd${teeName ? ` · ${teeName}` : ""}</span>`}
        </div>
      `}

      ${/* The one action worth offering, and only while it would do something. */
      !enabled &&
      html`
        <button
          class="ghost gd-enable"
          onClick=${() => {
            setAsked(true);
            onEnable();
          }}
        >
          <${Icon} name="pin" size=${18} /> Show distance to the green
        </button>
      `}

      ${showPlan &&
      planAvailable &&
      html`<${HolePlan}
        geo=${geo}
        at=${live?.coords ?? null}
        accuracyYards=${accuracy}
        paths=${paths}
        landmarks=${landmarks}
        others=${others}
        hazards=${hazards}
      />`}
    </div>
  `;
}
